import { FC, useMemo } from 'react';

import { GiftOrderForAdmin } from '@shared/api/giftOrder/giftOrder.interface';

import {
  BAR_COLORS,
  buildLaterStats,
  fmt,
  getNiceScale,
} from './analysisUtils';

interface Props {
  orderList: GiftOrderForAdmin[] | null;
}

export const LaterTab: FC<Props> = function LaterTab({ orderList }) {
  const laterData = useMemo(() => {
    if (!orderList) return null;
    return buildLaterStats(orderList);
  }, [orderList]);

  if (!laterData || laterData.totalVerified === 0) {
    return (
      <div className="rounded-lg bg-white p-8 text-center shadow">
        <p className="text-gray-500">
          해당 기간에 번호 인증된 쿠폰이 없습니다.
        </p>
      </div>
    );
  }

  const laterBins = laterData.bins;
  const laterBinScale = getNiceScale(
    Math.max(...laterBins.map((b) => b.count), 1),
  );
  const laterDonutData = [
    {
      key: 'immediate',
      label: '즉시 받기',
      count: laterData.immediateCount,
      rate:
        laterData.totalVerified > 0
          ? (laterData.immediateCount / laterData.totalVerified) * 100
          : 0,
      hex: '#10b981',
    },
    {
      key: 'later',
      label: '나중에 받기',
      count: laterData.laterCount,
      rate:
        laterData.totalVerified > 0
          ? (laterData.laterCount / laterData.totalVerified) * 100
          : 0,
      hex: '#f59e0b',
    },
  ];

  return (
    <div className="space-y-6">
      {/* 요약 카드 */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">인증 쿠폰</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">
            {fmt(laterData.totalVerified)}장
          </p>
          <p className="mt-1 text-xs text-gray-400">번호 인증을 거친 쿠폰</p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">즉시 받기</p>
          <p className="mt-1 text-2xl font-bold text-emerald-600">
            {fmt(laterData.immediateCount)}장
          </p>
          <p className="mt-1 text-xs text-gray-400">
            {laterData.totalVerified > 0
              ? (
                  (laterData.immediateCount / laterData.totalVerified) *
                  100
                ).toFixed(1)
              : '0.0'}
            %
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">나중에 받기</p>
          <p className="mt-1 text-2xl font-bold text-amber-600">
            {fmt(laterData.laterCount)}장
          </p>
          <p className="mt-1 text-xs text-gray-400">
            {laterData.totalVerified > 0
              ? (
                  (laterData.laterCount / laterData.totalVerified) *
                  100
                ).toFixed(1)
              : '0.0'}
            %
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">나중에 받기 → 수령 완료</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">
            {fmt(laterData.laterDeliveredCount)}장
          </p>
          <p className="mt-1 text-xs text-gray-400">
            {laterData.laterCount > 0
              ? (
                  (laterData.laterDeliveredCount / laterData.laterCount) *
                  100
                ).toFixed(1)
              : '0.0'}
            % 수령
          </p>
        </div>
      </div>

      {/* 즉시 vs 나중에 받기 비율 (도넛) */}
      <div className="flex flex-col rounded-lg bg-white p-6 shadow">
        <h2 className="mb-1 text-lg font-bold text-gray-800">
          즉시 / 나중에 받기 비율
        </h2>
        <p className="mb-4 text-xs text-gray-400">
          번호 인증된 쿠폰 {fmt(laterData.totalVerified)}장 기준 · 나중에 받기 =
          인증 후 1시간 이상 대기
        </p>
        <div className="flex min-h-[300px] flex-col items-center justify-center gap-8 lg:flex-row lg:justify-around">
          {/* 도넛 */}
          <div
            className="relative shrink-0"
            style={{ width: 260, height: 260 }}
          >
            <svg viewBox="0 0 260 260" className="h-full w-full -rotate-90">
              {(() => {
                const r = 96;
                const c = 2 * Math.PI * r;
                let acc = 0;
                return laterDonutData.map((g) => {
                  if (g.rate <= 0) return null;
                  const seg = (g.rate / 100) * c;
                  const offset = -(acc / 100) * c;
                  acc += g.rate;
                  return (
                    <circle
                      key={g.key}
                      cx={130}
                      cy={130}
                      r={r}
                      fill="none"
                      stroke={g.hex}
                      strokeWidth={36}
                      strokeDasharray={`${seg} ${c}`}
                      strokeDashoffset={offset}
                      className="transition-opacity hover:opacity-80"
                    />
                  );
                });
              })()}
            </svg>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-[11px] text-gray-400">인증</span>
              <span className="text-lg font-bold text-gray-900">
                {fmt(laterData.totalVerified)}장
              </span>
            </div>
          </div>
          {/* 범례 */}
          <div className="w-full max-w-sm space-y-2">
            {laterDonutData.map((g) => (
              <div key={g.key} className="flex items-center gap-2 text-sm">
                <span
                  className="inline-block h-3 w-3 shrink-0 rounded-sm"
                  style={{ backgroundColor: g.hex }}
                />
                <span className="w-24 shrink-0 text-gray-600">{g.label}</span>
                <span className="flex-1 text-right font-bold text-gray-800">
                  {g.rate.toFixed(1)}%
                </span>
                <span className="w-16 shrink-0 text-right text-gray-600">
                  {fmt(g.count)}장
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 나중에 받기 → 수령까지 기간 분포 */}
      <div className="flex flex-col rounded-lg bg-white p-6 shadow">
        <h2 className="mb-1 text-lg font-bold text-gray-800">
          나중에 받기 → 수령까지 기간
        </h2>
        <p className="mb-6 text-xs text-gray-400">
          나중에 받기 {fmt(laterData.laterCount)}장의 대기 일수 분포
          (verifiedAt → requestedAt) · 미수령 포함
        </p>
        {laterData.laterCount === 0 ? (
          <p className="py-8 text-center text-sm text-gray-400">
            나중에 받기 건이 없습니다.
          </p>
        ) : (
          <div className="mx-auto flex w-full max-w-3xl flex-col">
            <div className="flex min-h-[280px] flex-1">
              {/* Y축 눈금 */}
              <div className="relative mr-2 shrink-0" style={{ width: 32 }}>
                {laterBinScale.ticks.map((t) => (
                  <span
                    key={t}
                    className="absolute right-0 translate-y-1/2 text-[10px] leading-none text-gray-400"
                    style={{
                      bottom: `${(t / laterBinScale.niceMax) * 100}%`,
                    }}
                  >
                    {fmt(t)}
                  </span>
                ))}
              </div>
              {/* 플롯 영역 */}
              <div className="relative flex-1">
                {laterBinScale.ticks.map((t) => (
                  <div
                    key={t}
                    className="pointer-events-none absolute inset-x-0 border-t border-gray-100"
                    style={{
                      bottom: `${(t / laterBinScale.niceMax) * 100}%`,
                    }}
                  />
                ))}
                <div className="absolute inset-0 flex items-end justify-around">
                  {laterBins.map((b, i) => {
                    const pct =
                      laterBinScale.niceMax > 0
                        ? (b.count / laterBinScale.niceMax) * 100
                        : 0;
                    return (
                      <div
                        key={b.label}
                        className="group relative flex h-full flex-col items-center justify-end"
                        style={{ flex: 1 }}
                      >
                        <div className="pointer-events-none absolute -top-1 z-10 hidden -translate-y-full rounded bg-gray-800 px-2 py-1 text-[10px] leading-tight whitespace-nowrap text-white shadow group-hover:block">
                          {b.label}: {fmt(b.count)}장 ({b.rate.toFixed(1)}%)
                        </div>
                        <span className="mb-1 text-[11px] font-bold text-gray-700">
                          {b.count > 0 ? fmt(b.count) : ''}
                        </span>
                        <div
                          className={`w-full max-w-[56px] rounded-t transition-all duration-500 ${
                            b.isUnreceived
                              ? 'bg-gray-300'
                              : (BAR_COLORS[i] ?? 'bg-gray-300')
                          }`}
                          style={{
                            height: `${pct}%`,
                            minHeight: b.count > 0 ? 4 : 0,
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            {/* X축 라벨 */}
            <div className="flex">
              <div className="mr-2 shrink-0" style={{ width: 32 }} />
              <div className="flex flex-1 justify-around">
                {laterBins.map((b) => (
                  <div
                    key={b.label}
                    className="mt-2 text-center"
                    style={{ flex: 1 }}
                  >
                    <span className="block text-[11px] font-medium text-gray-500">
                      {b.label}
                    </span>
                    <span className="block text-[10px] text-gray-400">
                      {b.rate.toFixed(1)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
